import { useCallback, useEffect, useRef, useState } from "react";
import {
  EMPTY_VIEWER_ADMIN_DEPLOYMENT_RUNTIME_ACTIONS,
  EMPTY_VIEWER_ADMIN_DEPLOYMENT_RUNTIME_STATE,
  type ViewerAdminDeploymentRuntimeActions,
  type ViewerAdminDeploymentRuntimeState,
} from "./useViewerAdminDeploymentRuntime";

interface UseViewerAdminDeploymentBridgeStateParams {
  enabled: boolean;
  companyId: string | null;
  productId: string | null;
}

function hasEqualJobStatus(
  left: ViewerAdminDeploymentRuntimeState["jobStatus"],
  right: ViewerAdminDeploymentRuntimeState["jobStatus"],
) {
  if (!left && !right) {
    return true;
  }

  if (!left || !right) {
    return false;
  }

  return (
    left.jobId === right.jobId &&
    left.status === right.status &&
    left.targetReleaseId === right.targetReleaseId &&
    left.errorMessage === right.errorMessage
  );
}

export function useViewerAdminDeploymentBridgeState(
  params: UseViewerAdminDeploymentBridgeStateParams,
) {
  const { enabled, companyId, productId } = params;
  const [adminDeploymentRuntime, setAdminDeploymentRuntime] =
    useState<ViewerAdminDeploymentRuntimeState>(
      EMPTY_VIEWER_ADMIN_DEPLOYMENT_RUNTIME_STATE,
    );
  const adminDeploymentRuntimeActionsRef =
    useRef<ViewerAdminDeploymentRuntimeActions>(
      EMPTY_VIEWER_ADMIN_DEPLOYMENT_RUNTIME_ACTIONS,
    );

  const onStateChange = useCallback(
    (nextState: ViewerAdminDeploymentRuntimeState) => {
      setAdminDeploymentRuntime((previousState) => {
        if (
          previousState.status === nextState.status &&
          previousState.error === nextState.error &&
          previousState.previewLabel === nextState.previewLabel &&
          previousState.isPreviewPending === nextState.isPreviewPending &&
          previousState.isDeployPending === nextState.isDeployPending &&
          previousState.canCreatePreview === nextState.canCreatePreview &&
          previousState.canDeploy === nextState.canDeploy &&
          previousState.deploymentError === nextState.deploymentError &&
          hasEqualJobStatus(previousState.jobStatus, nextState.jobStatus)
        ) {
          return previousState;
        }

        return nextState;
      });
    },
    [],
  );

  const onActionsChange = useCallback(
    (nextActions: ViewerAdminDeploymentRuntimeActions) => {
      adminDeploymentRuntimeActionsRef.current = nextActions;
    },
    [],
  );

  useEffect(() => {
    if (!enabled) {
      setAdminDeploymentRuntime(EMPTY_VIEWER_ADMIN_DEPLOYMENT_RUNTIME_STATE);
      adminDeploymentRuntimeActionsRef.current =
        EMPTY_VIEWER_ADMIN_DEPLOYMENT_RUNTIME_ACTIONS;
      return;
    }

    // Route identity changes must clear stale deployment state and actions
    // before the next runtime bridge payload arrives.
    setAdminDeploymentRuntime({
      ...EMPTY_VIEWER_ADMIN_DEPLOYMENT_RUNTIME_STATE,
      status: "loading",
    });
    adminDeploymentRuntimeActionsRef.current =
      EMPTY_VIEWER_ADMIN_DEPLOYMENT_RUNTIME_ACTIONS;
  }, [enabled, companyId, productId]);

  const createPreview = useCallback(async () => {
    return adminDeploymentRuntimeActionsRef.current.createPreview();
  }, []);

  const deploy = useCallback(async () => {
    return adminDeploymentRuntimeActionsRef.current.deploy();
  }, []);

  return {
    adminDeploymentRuntime,
    onStateChange,
    onActionsChange,
    createPreview,
    deploy,
  };
}
